import { useState } from "react";
import { toast } from "@/state/hooks";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { Card, CardContent } from "@/ui/components/ui/card";
import { ListContainer } from "@/ui/components/shared/ListItem";
import { UserProfilePicture } from "@/ui/components/UserProfilePicture";
import { useActions } from "@/state/legacy_module_api";
import { Contact, contactSchemaForClient } from "@/data/schemas";
import {
  FULL_HANDLE_ERROR_MESSAGE,
  normalizeFullHandle,
} from "@/features/contacts/handleUtils";

type Props = {
  onBack?: () => void;
  onUserAdded?: (user: Contact) => void;
};

const SearchUserPane = ({ onBack, onUserAdded }: Props) => {
  const { findUser, addContact } = useActions().user;
  const [usernameWithDiscriminator, setUsernameWithDiscriminator] =
    useState("");
  const [user, setUser] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(false);
  const [addingContact, setAddingContact] = useState(false);

  const onSearch = async () => {
    const handle = normalizeFullHandle(usernameWithDiscriminator);
    if (!handle) {
      toast.error(FULL_HANDLE_ERROR_MESSAGE);
      return;
    }
    setLoading(true);
    try {
      const result = await findUser({ handle });
      if (result && result.user) {
        setUser({ ...result.user, discriminator: result.discriminator });
      } else {
        throw new Error("Contact not found");
      }
    } catch (err: any) {
      toast.error(err.message || "Contact not found");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const onAddContact = async () => {
    if (!user) return;
    setAddingContact(true);
    try {
      const clientUser = contactSchemaForClient.parse(user);
      await addContact({ user: clientUser });
      toast.success("Contact added successfully");
      onUserAdded?.(clientUser);
    } catch (err: any) {
      toast.error(err.message || "Failed to add contact");
    } finally {
      setAddingContact(false);
    }
  };

  return (
    <div data-name="search-user-pane" className="h-full overflow-auto space-y-4 p-4">
      <Card>
        <CardContent className="pt-6">
          {onBack && (
            <Button
              data-name="back-button"
              variant="ghost"
              size="sm"
              onClick={onBack}
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="ml-2">Back</span>
            </Button>
          )}

          <h2 className="text-xl font-semibold mb-4">Find a Contact</h2>

          <Label htmlFor="username">Username.Discriminator</Label>
          <Input
            id="username"
            value={usernameWithDiscriminator}
            onChange={(e) => setUsernameWithDiscriminator(e.target.value)}
            placeholder="username.1234"
            className="mb-4"
          />

          <Button onClick={onSearch} disabled={loading} className="w-full">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Searching
              </>
            ) : (
              "Find Contact"
            )}
          </Button>
        </CardContent>
      </Card>

      {user && (
        <Card>
          <CardContent className="pt-6">
            <ListContainer>
              <div className="flex items-center gap-4">
                <UserProfilePicture user={user} width={48} />
                <div>
                  <div className="font-medium">
                    {user.fullHandle || user.username || user.deviceId}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    @{user.fullHandle || user.username || user.deviceId}
                  </div>
                </div>
              </div>
            </ListContainer>

            <Button
              onClick={onAddContact}
              disabled={addingContact}
              className="w-full mt-4"
            >
              {addingContact ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding
                </>
              ) : (
                "Add to Contacts"
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SearchUserPane;
